import { renderXAxisLabels, renderYAxisLabels } from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable, TpDataTableColumn, VALTYPES } from '../../../../data/interfaces';
import { LEGEND_ITEM_TYPES, renderLegendItems, renderYLabel, TpLegendItem } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import { createNumericalScaleStops } from '../../helpers/colorMappingInfo';
import { chartDims, convertColumnDataToDisplayString, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { getThemedGrayFraction } from '../../../themes/themes';
import { getLabelSpaceSize } from '../../helpers/configParams';
import { getUserError } from '../../../../components/miscUtils';
import { getNumericalAggregator } from '../../helpers/aggregators';
import { getNumericalColorSet } from '../../helpers/colors';
import { normalizerStandard } from '../../helpers/vectorNormalizers';
import { sortCategories, TpCategoryInfo } from '../../helpers/categoricalSorters';

interface TpPlotCatInfo extends TpCategoryInfo {
  positionIndex: number;
}

interface TpPlotPropertyInfo {
  columnInfo: TpDataTableColumn;
  data: number[];
  cats: {
    data: number[];
    dataSelected: number[];
    aggDiscSize: number;
    aggDiscSizeSelected: number;
    aggColorValue: number;
    aggColorValueSelected: number;
  }[];
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;

  const dataHoverLabel = boundData.bindings.hoverLabel?.data;
  const title = generateChartTitle(dataContext, dataBinding, config, '');
  const dataItemId = boundData.bindings.itemId.data;
  const dataCategories = boundData.bindings.categories.data;
  const catColumnInfo = boundData.bindings.categories.columnInfo;
  const itemCount = dataCategories.length;

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;
  const getItemIndexById = boundData.getItemIndexById;

  // Create the categories (= rows in the plot)
  const categoryMap: { [cat: string]: TpPlotCatInfo } = {};
  const categories: TpPlotCatInfo[] = [];
  for (let i = 0; i < itemCount; i++) {
    const catValue = dataCategories[i];
    if (!categoryMap[catValue]) {
      const cat: TpPlotCatInfo = {
        value: catValue,
        displayString: convertColumnDataToDisplayString(catColumnInfo, catValue),
        count: 0,
        countSelected: 0,
        positionIndex: -1,
      };
      categories.push(cat);
      categoryMap[catValue] = cat;
    }
    categoryMap[catValue].count++;
    if (isSelectedItem(selectedItemsMap, dataItemId[i])) categoryMap[catValue].countSelected++;
  }

  if (categories.length > 500) throw getUserError('Too many categories');
  sortCategories(categories, config.sortCatsBy);

  const catCount = categories.length;
  let posIndex = 0;
  for (const cat of categories) {
    cat.positionIndex = posIndex;
    posIndex++;
  }

  // Create the properties (= columns in the plot)
  const properties: TpPlotPropertyInfo[] = boundData.compoundBindings.properties.map((prop) => {
    if (prop.columnInfo.valueType !== VALTYPES.NUMBER)
      throw getUserError(`Property should be numerical: ${prop.columnInfo.id}`);
    const property: TpPlotPropertyInfo = {
      columnInfo: prop.columnInfo,
      data: prop.data,
      cats: categories.map((cat) => ({
        data: [],
        dataSelected: [],
        aggDiscSize: 0,
        aggDiscSizeSelected: 0,
        aggColorValue: 0,
        aggColorValueSelected: 0,
      })),
    };
    return property;
  });

  // populate properties with values
  for (let i = 0; i < itemCount; i++) {
    const catIndex = categoryMap[dataCategories[i]].positionIndex;
    for (const prop of properties) {
      const value = prop.data[i];
      if (!isNaN(value)) {
        prop.cats[catIndex].data.push(value);
        if (isSelectedItem(selectedItemsMap, dataItemId[i])) prop.cats[catIndex].dataSelected.push(value);
      }
    }
  }

  // calculate the aggregated values per cell
  const aggDisc = getNumericalAggregator(config.discSizeAggregator);
  const aggColor = getNumericalAggregator(config.discColorAggregator);
  for (const prop of properties) {
    for (const cell of prop.cats) {
      cell.aggDiscSize = aggDisc.perform(cell.data);
      cell.aggDiscSizeSelected = aggDisc.perform(cell.dataSelected);
      cell.aggColorValue = aggColor.perform(cell.data);
      cell.aggColorValueSelected = aggColor.perform(cell.dataSelected);
    }
  }

  // Normalize color values by column, if chosen
  const isColorColumnNormalized = config.discColorTransformation == 'columnNormalized';
  if (isColorColumnNormalized) {
    for (const prop of properties) {
      const normValues = normalizerStandard.compute(prop.cats.map((cat) => cat.aggColorValue));
      const normValuesSelected = normalizerStandard.compute(prop.cats.map((cat) => cat.aggColorValueSelected));
      for (const cell of prop.cats) {
        cell.aggColorValue = normalizerStandard.apply(normValues, cell.aggColorValue);
        cell.aggColorValueSelected = normalizerStandard.apply(normValuesSelected, cell.aggColorValueSelected);
      }
    }
  }

  // calculate ranges
  let minColorValue = 1e99;
  let maxColorValue = -1e99;
  let maxDiscSize = 1e-99;
  for (const prop of properties) {
    for (const cell of prop.cats) {
      if (!isNaN(cell.aggDiscSize)) maxDiscSize = Math.max(maxDiscSize, cell.aggDiscSize);
      if (!isNaN(cell.aggDiscSizeSelected)) maxDiscSize = Math.max(maxDiscSize, cell.aggDiscSizeSelected);
      if (!isNaN(cell.aggColorValue)) {
        maxColorValue = Math.max(maxColorValue, cell.aggColorValue);
        minColorValue = Math.min(minColorValue, cell.aggColorValue);
      }
      if (!isNaN(cell.aggColorValueSelected)) {
        maxColorValue = Math.max(maxColorValue, cell.aggColorValueSelected);
        minColorValue = Math.min(minColorValue, cell.aggColorValueSelected);
      }
    }
  }

  const propertyCount = properties.length;

  const hasLegend = true;
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const colorSchemeNumerical = config.colorSchemeNumerical;
  const colors = getNumericalColorSet(colorSchemeNumerical).colors;
  const colorGammaFactor = (config.colorGammaNumerical / 10) ** 3 / 0.5 ** 3;

  function renderRowLabels(renderContext: TpAreaRenderContext) {
    renderYAxisLabels(
      renderContext,
      selectedItemsMap,
      categories.map((cat) => cat.displayString),
    );
  }

  function renderColumnLabels(renderContext: TpAreaRenderContext) {
    renderXAxisLabels(
      renderContext,
      properties.map((prop) => prop.columnInfo.name),
    );
  }

  function renderData(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    ctx.strokeStyle = getThemedGrayFraction(0.9).toCanvasString();
    ctx.lineWidth = renderContext.pixelRatio * 0.5;
    for (let idxX = 0; idxX < propertyCount; idxX++) {
      const xCent = mapx((idxX + 0.5) / propertyCount);
      ctx.beginPath();
      ctx.moveTo(xCent, mapy(0));
      ctx.lineTo(xCent, mapy(1));
      ctx.stroke();
    }
    for (let idxY = 0; idxY < catCount; idxY++) {
      const yCent = mapy((idxY + 0.5) / catCount);
      ctx.beginPath();
      ctx.moveTo(mapx(0), yCent);
      ctx.lineTo(mapx(1), yCent);
      ctx.stroke();
    }

    const unSelectedFill = getThemedGrayFraction(0.85).toCanvasString();
    const maxRadius = Math.min(
      (Math.abs(mapx(1) - mapx(0)) / propertyCount) * 0.25,
      (Math.abs(mapy(1) - mapy(0)) / catCount) * 0.25,
    );

    const gammaFactorRadius = Math.exp(config.gammaRadius / 2) / Math.exp(5 / 2);

    for (let idxX = 0; idxX < propertyCount; idxX++) {
      const xCent = mapx((idxX + 0.5) / propertyCount);
      for (let idxY = 0; idxY < catCount; idxY++) {
        const yCent = mapy((idxY + 0.5) / catCount);
        const cell = properties[idxX].cats[idxY];

        if (!isNaN(cell.aggDiscSize) && !isNaN(cell.aggColorValue)) {
          const rd = Math.sqrt(cell.aggDiscSize / maxDiscSize) ** gammaFactorRadius * maxRadius;
          ctx.beginPath();
          ctx.moveTo(xCent, yCent);
          ctx.arc(xCent, yCent, pixelConv(rd), 0, 2 * Math.PI);
          ctx.closePath();
          ctx.fillStyle = unSelectedFill;
          ctx.fill();
        }
        if (!isNaN(cell.aggDiscSizeSelected) && !isNaN(cell.aggColorValueSelected)) {
          const rd = Math.sqrt(cell.aggDiscSizeSelected / maxDiscSize) ** gammaFactorRadius * maxRadius;
          ctx.beginPath();
          ctx.moveTo(xCent, yCent);
          ctx.arc(xCent, yCent, pixelConv(rd), 0, 2 * Math.PI);
          ctx.closePath();
          let colorFrac: number;
          if (maxColorValue - minColorValue < 1e-9) {
            colorFrac = 0.5;
          } else {
            colorFrac =
              Math.max(
                1e-99,
                Math.min(1, (cell.aggColorValueSelected - minColorValue) / (maxColorValue - minColorValue)),
              ) ** colorGammaFactor;
          }
          const color = colors[Math.round(Math.max(0, Math.min(colors.length - 1, colorFrac * colors.length)))];
          ctx.fillStyle = color.toCanvasString();
          ctx.fill();
        }
      }
    }
  }

  const legendDiscSize = aggDisc.displayName;
  let legendDiscColor = aggColor.displayName;
  if (isColorColumnNormalized) legendDiscColor += ' (column normalized)';

  const legendItems: TpLegendItem[] = [
    {
      type: LEGEND_ITEM_TYPES.NumericalColors,
      colorColumnInfo: null,
      customTitle: legendDiscColor,
      colorValueStrings: colors.map((color) => color.toCanvasString()),
      minValColor: minColorValue,
      maxValColor: maxColorValue,
      gammaFactor: colorGammaFactor,
      numericalLegendItems: createNumericalScaleStops(minColorValue, maxColorValue),
    },
    {
      type: LEGEND_ITEM_TYPES.PointSize,
      sizeColumnInfo: null,
      customTitle: legendDiscSize,
      minValPointSize: 0,
      maxValPointSize: maxDiscSize,
    },
  ];

  function renderLegend(renderContext: TpAreaRenderContext) {
    renderLegendItems(renderContext, legendItems);
  }

  const padding = chartDims.padding;

  const xAxisHeight = getLabelSpaceSize(config.labelSpaceProps);
  const yAxisWidth = getLabelSpaceSize(config.labelSpaceCats);

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const zpLogicalRangeY = createLogicalRange(1, 0);
  const zpRenderRangeX = createRenderRange(RenderMinDiffAbs(padding + yAxisWidth), RenderMaxDiffAbs(padding + 0));
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
  );

  const areaData: TpChartAreaDefinition = {
    id: 'data',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    render: renderData,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaData,
    {
      id: 'xaxis',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      zoomPanX: true,
      render: renderColumnLabels,
    },
    {
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + yAxisWidth)),
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderRowLabels,
    },
    {
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, '', title),
    },
  ];

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaData,
    dataTable,
    dataItemId,
    properties,
    categories,
    legendDiscSize,
    legendDiscColor,
    getItemIndexById: boundData.getItemIndexById,
    title,
  };
}
